import { action, get } from '@ember/object';
import { isEmpty, isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { getChangesetErrorsForProperty } from 'donor-elf-web/lib/changeset-utils';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import fuzzysort from 'fuzzysort';

interface ComponentArgs {
  changeset: any;
  property: string;
  valIsObjectKey: boolean; // if true, the property value is in this format: { "Selected Option": true }
  selectedVal: any; // either @changeset and @property need to be supplied or @selectedVal
  options: SelectOption[];
  booleanOptions?: boolean; // if true, then it creates Yes/No options
  showSearchWhenOptionsLengthGreaterThan?: number; // default 20
  placeholder?: string;
  disabled?: boolean; // default false
  showValidationErrors?: boolean; // default false
  horizontalPosition?: string; // default 'auto-right'
  dropdownInitiallyOpened?: boolean; // default false. if true, dropdown is open when initially rendered
  dropdownMatchTriggerWidth?: boolean; // default false. if true, dropdown matches width of textbox
  dropdownMaxHeightClass?: string;
  dropdownContentClass?: string; // 'tw-z-1100' can be used when the component is in a <Modal /> component
  onChange: (val: any) => void;
}

// If 'Enter' is pressed, then it will selected the highlighted index (or first index if one isn't highlighted)
export default class FormUnderlineSelectComponent extends Component<ComponentArgs> {
  @tracked _cache: any = {};
  @tracked searchVal: string = '';
  @tracked showSelectedIndex = false;
  @tracked highlightedIndex?: number;
  dropdown?: any;

  get options(): SelectOption[] {
    if (this.args.booleanOptions) {
      return [
        {
          val: true,
          label: 'Yes',
        },
        {
          val: false,
          label: 'No',
        },
      ];
    } else {
      return this.args.options;
    }
  }

  get horizontalPosition() {
    return this.args.horizontalPosition || 'auto-right';
  }

  get dropdownMaxHeightClass() {
    return this.args.dropdownMaxHeightClass || 'tw-max-h-96';
  }

  get valIsObjectKey() {
    return this.args.valIsObjectKey ?? false;
  }

  get changesetValue() {
    // don't let get() raise an error when @changeset or @property are null
    let val = get(this.args.changeset || {}, this.args.property || '');
    if (this.valIsObjectKey && val != null && typeof val === 'object') {
      val = Object.keys(val)[0];
    }
    return val;
  }

  get selectedVal() {
    return this.args.selectedVal ?? this.changesetValue;
  }

  get selectedOption() {
    if (this._cache.selectedOption) {
      return this._cache.selectedOption;
    }
    let { selectedVal } = this;
    return (this._cache.selectedOption = this.options.find(
      (x) => x.val === selectedVal,
    ));
  }

  get notSelectedAndHasPlaceholder() {
    return (
      isEmpty(this.selectedOption?.val) && isPresent(this.args.placeholder)
    );
  }

  get showSearch() {
    return (
      this.options.length >
      (this.args.showSearchWhenOptionsLengthGreaterThan ?? 20)
    );
  }

  get filteredOptions(): SelectOption[] {
    if (this.searchVal.length < 2) {
      return this.options;
    } else {
      return fuzzysort
        .go(this.searchVal, this.options, { key: 'label' })
        .map((x) => x.obj);
    }
  }

  get errorMessage() {
    return getChangesetErrorsForProperty(this.args.changeset, this.args.property)?.join(', ');
  }

  get showError() {
    return this.args.showValidationErrors && isPresent(this.errorMessage);
  }

  @action
  handleDropdownOnClose(_dropdown: any, e?: any) {
    if (
      e?.type === 'keydown' &&
      e.key === 'Enter' &&
      this.highlightedIndex != null
    ) {
      let selectedOption = this.filteredOptions[this.highlightedIndex];
      if (selectedOption != null) {
        this.setSelectedVal(selectedOption.val);
      }
    }
  }

  // can't use `@onOpen` event on <BasicDropdown /> because it's not called
  // when `@initiallyOpened={{true}}`
  @action
  dropdownContentDidInsert(dropdown: any) {
    this.dropdown = dropdown;
    // have to wait one cycle else it will pickup the keydown event
    // immediately preceding the dropdown opening
    setTimeout(() => {
      if (!this.isDestroying && !this.isDestroyed) {
        document.addEventListener('keydown', this.handleDocumentKeydown);
      }
    });
  }

  @action
  dropdownContentWillDestroy() {
    this.highlightedIndex = undefined;
    this.dropdown = undefined;
    this.searchVal = '';
    document.removeEventListener('keydown', this.handleDocumentKeydown);
  }

  @action
  handleDocumentKeydown(e: KeyboardEvent) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (this.highlightedIndex == null) {
        this.highlightedIndex = -1;
      }
      if (e.key === 'ArrowDown') {
        this.highlightedIndex += 1;
        if (this.highlightedIndex >= this.filteredOptions.length) {
          this.highlightedIndex = this.filteredOptions.length - 1;
        }
      } else {
        this.highlightedIndex -= 1;
        if (this.highlightedIndex < 0) {
          this.highlightedIndex = 0;
        }
      }
    } else if (e.key === 'Enter') {
      // if Enter is pressed while on the search input element in the dropdown
      if ((e?.target as HTMLInputElement)?.closest?.('.de-dropdown-content')) {
        if (this.searchVal != (e.target as HTMLInputElement)!.value) {
          this.handleSearchInput(e);
        }
      }
      if (this.highlightedIndex != null) {
        let selectedOption = this.filteredOptions[this.highlightedIndex];
        if (selectedOption != null) {
          this.setSelectedVal(selectedOption.val);
        }
      } else {
        let firstOption = this.filteredOptions[0];
        if (firstOption) {
          this.setSelectedVal(firstOption.val);
        }
      }
    }
  }

  @action
  handleSearchInput(e: any) {
    if (this.searchVal != e.target!.value) {
      this.highlightedIndex = undefined;
    }
    this.searchVal = e.target!.value;
    if (this.searchVal == null) {
      this.searchVal = '';
    }
  }

  @action
  handleSelectedValUpdated() {
    this._cache = {};
  }

  @action
  setSelectedVal(val: any) {
    this.searchVal = '';
    if (this.args.changeset) {
      if (this.valIsObjectKey) {
        let objectVal: any = {};
        objectVal[val] = true;
        this.args.changeset.set(this.args.property, objectVal);
      } else {
        this.args.changeset.set(this.args.property, val);
      }
    }
    this.args.onChange?.(val);
    if (this.dropdown?.isOpen) {
      this.dropdown.actions.close();
    }
  }
}
